
import { Vue, Component, Prop } from 'vue-property-decorator';
import { ChartLoadingStatus, PlotlyTraceType } from '@/types';
import { ScenarioNameType, TimelineFormattedType } from '@/types';
import { colorMap } from '@/assets/colors/colors';
import ChartComponent from '@/components/DataTab/Charts/ChartComponents/ChartComponent.vue';
import { sortMonthsChronologicallyWithValues } from '@/helpers/utils';

@Component({
  components: {
    ChartComponent,
  },
})
export default class TimelineChart extends Vue {
  @Prop() readonly timelineData!: TimelineFormattedType[];
  @Prop() readonly timelineDataStatus!: ChartLoadingStatus;
  @Prop() readonly chartId!: number;
  @Prop({ required: false, default: undefined }) readonly chartSelection!:
    | string
    | undefined;
  @Prop({ default: 'year' }) readonly timeUnit!: 'month' | 'year';
  @Prop({ type: Boolean, default: false }) readonly hideUnit!: boolean;

  get scenarioList(): ScenarioNameType[] {
    let scenarios = this.$store.getters.selectedScenarioList;
    if (this.chartId == 7 && !this.selectedArea.name.includes('Yampa')) {
      scenarios = scenarios.filter(
        (scenario: string) => scenario !== 'Baseline',
      );
    }
    return scenarios;
  }

  get unit() {
    return this.$t(`data.chartTables.${this.chartId}.unit`);
  }

  get chartData() {
    var initialData: PlotlyTraceType[] = [];
    const localeTimeUnit = this.$t(
      `data.chartTables.timeUnits.${this.timeUnit}`,
    );

    this.scenarioList.forEach(item => {
      initialData.push({
        x: [],
        y: [],
        name: item,
        type: 'scatter',
        mode: 'lines+markers',
        marker: {
          color: colorMap[item],
          size: 8,
          symbol: 'circle-open',
        },
        hovertemplate: `<b>${localeTimeUnit}: </b>%{x}<br><b>${item}:</b> %{y:,.2f} ${this.unit}<extra></extra>`,
        line: {
          dash: item === 'Baseline' ? 'dot' : undefined,
        },
      });
    });

    this.timelineData.forEach(obj => {
      initialData.forEach((object: PlotlyTraceType) => {
        if (obj.scenario === object.name) {
          // only show baseline on yampa
          if (!object.x.includes(obj.date)) {
            object.x.push(obj.date);
            object.x.sort();
          }

          object.y.push(obj.value);
        }
      });
    });

    // Sort the PlotlyTraceType X values chronologically, not alphabetically
    initialData.forEach(obj => {
      const { sortedX, sortedY } = sortMonthsChronologicallyWithValues(
        obj.x,
        obj.y,
      );
      obj.x = sortedX;
      obj.y = sortedY;
    });

    return initialData;
  }

  get selectedArea() {
    return this.$store.getters.selectedArea;
  }

  get layout() {
    const label = this.chartSelection
      ? this.$t(
          `data.chartTables.${this.chartId}.axisLabels.y.${this.chartSelection}`,
        )
      : this.$t(`data.chartTables.${this.chartId}.axisLabels.y`);

    return {
      yaxis: {
        fixedrange: true,
        automargin: true,
        title: {
          text: this.hideUnit ? label : `${label} (${this.unit})`,
          standoff: 10,
        },
      },
      xaxis: {
        fixedrange: true,
      },
      legend: { orientation: 'h', y: -0.2 },
      paper_bgcolor: 'transparent',
      plot_bgcolor: 'transparent',
      hovermode: 'closest',
      // removes empty heading above graph
      margin: {
        l: 25,
        r: 15,
        b: 50,
        t: 5,
        pad: 3,
      },
    };
  }
}
